import * as PIXI from 'pixi.js';
import GameConfig from '../../game/GameConfig';
import GameUtils from '../../game/GameUtils';
import GameModel from '../../game/GameModel';
import {MapState} from '../../common/map/MapState';
import {clamp} from 'src/utils';
import {cannonRenderer} from './CannonRenderer';
import {heldRenderer} from './HeldRenderer';
import {spawnRenderer} from './SpawnRenderer';

import Container = PIXI.Container;
import Sprite = PIXI.Sprite;

class MapRenderer {
    private stage: Container;
    private viewport: Container;
    public terrainContainer: Container;
    public heldContainer: Container;
    public castleContainer: Container;
    public cannonsContainer: Container;
    public wallsContainer: Container;
    public cannonballsContainer: Container;
    public cursorContainer: Container;
    public uiContainer: Container;

    public setupContainers(stage: Container, viewport: Container): void {
        this.stage = stage;
        this.viewport = viewport;
        this.teardown();
        this.stage.addChild(this.viewport);

        this.terrainContainer = new Container();
        this.viewport.addChild(this.terrainContainer);

        GameModel.heldContainer = new Container();
        this.viewport.addChild(GameModel.heldContainer);

        this.castleContainer = new Container();
        this.viewport.addChild(this.castleContainer);

        GameModel.cannonsContainer = new Container();
        this.viewport.addChild(GameModel.cannonsContainer);

        GameModel.wallsContainer = new Container();
        this.viewport.addChild(GameModel.wallsContainer);

        GameModel.cannonballsContainer = new Container();
        this.viewport.addChild(GameModel.cannonballsContainer);

        GameModel.cursorContainer = new Container();
        this.viewport.addChild(GameModel.cursorContainer);

        GameModel.uiContainer = new Container();
        this.stage.addChild(GameModel.uiContainer);
    }

    public teardown(): void {
        cannonRenderer.clear();
        heldRenderer.clear();
        [...this.viewport.children].forEach(child => this.viewport.removeChild(child));
        [...this.stage.children].forEach(child => this.stage.removeChild(child));
    }

    public renderMap(map: MapState): void {
        this.makeWater(map);
        this.makeLand(map);
        spawnRenderer.redraw(map);
        this.makeCannons(map);
    }

    private makeCannons(map: MapState): void {
        map.cannons.forEach(cannon => cannonRenderer.createCannon(cannon));
    }

    private makeWater(map: MapState): void {
        for (let r = 0; r < map.height * 2; r++) {
            for (let c = 0; c < map.width * 2; c++) {
                const sprite = this.makeWaterTile(r, c);
                this.terrainContainer.addChild(sprite);
            }
        }
    }

    private makeWaterTile(innerR: number, innerC: number): Sprite {
        const texture = GameUtils.spriteTexture(`tiles/${this.calcWaterBitmap()}`);
        const sprite = new Sprite(texture);
        const size = GameConfig.tileSize / 2;
        sprite.position.set(innerC * size, innerR * size);
        return sprite;
    }

    private calcWaterBitmap(): string {
        return 'water';
    }

    private makeLand(map: MapState): void {
        for (let r = 0; r < map.height; r++) {
            for (let c = 0; c < map.width; c++) {
                const sprite = this.makeTile(map, r, c);
                this.terrainContainer.addChild(sprite);
            }
        }
    }

    private makeTile(map: MapState, r: number, c: number): Sprite {
        const sprite = new Sprite();
        sprite.addChild(this.makeInnerTile(map, r, c, 0, 0));
        sprite.addChild(this.makeInnerTile(map, r, c, 1, 0));
        sprite.addChild(this.makeInnerTile(map, r, c, 0, 1));
        sprite.addChild(this.makeInnerTile(map, r, c, 1, 1));
        const size = GameConfig.tileSize;
        sprite.position.set(c * size, r * size);
        return sprite;
    }

    private makeInnerTile(
        map: MapState,
        r: number,
        c: number,
        innerR: number,
        innerC: number
    ): Sprite {
        const tile = this.calcBitmap(map, r + innerR * 0.5, c + innerC * 0.5);
        const texture = GameUtils.spriteTexture(`tiles/${tile}`);
        const sprite = new Sprite(texture);
        const size = GameConfig.tileSize / 2;
        sprite.position.set(innerC * size, innerR * size);
        return sprite;
    }

    private calcBitmap(map: MapState, innerR: number, innerC: number): string {
        const offset = 0.25;
        const tl = this.calcCorner(map, innerR - offset, innerC - offset);
        const tr = this.calcCorner(map, innerR - offset, innerC + offset);
        const bl = this.calcCorner(map, innerR + offset, innerC - offset);
        const br = this.calcCorner(map, innerR + offset, innerC + offset);
        const bitmap = `${tl}${tr}${bl}${br}`;
        if (bitmap === '1111') {
            const num = Math.floor(Math.random() * 9);
            return `${bitmap}-alt${num}`;
        }
        return bitmap;
    }

    private calcCorner(map: MapState, subR: number, subC: number): number {
        let sum = 0;
        for (let r = subR; r <= subR + 0.5; r += 0.5) {
            for (let c = subC; c <= subC + 0.5; c += 0.5) {
                sum += this.calcSafe(map, r, c);
            }
        }
        return sum < 3 ? 0 : 1;
    }

    private calcSafe(map: MapState, subR: number, subC: number): number {
        const r = clamp(Math.floor(subR), 0, map.height - 1);
        const c = clamp(Math.floor(subC), 0, map.width - 1);
        return map.terrain[r][c].kind === 'land' ? 1 : 0;
    }
}

export default new MapRenderer();



// WEBPACK FOOTER //
// ./src/Pixi/map/MapRenderer.ts